import React, { useState, useEffect, useCallback } from "react";
import {
  collection, getDocs, addDoc, setDoc, deleteDoc,
  doc, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";

/* ── CONSTANTS ──────────────────────────────────────────────────── */
const SKILL_ICON_KEYS = [
  "FaBalanceScale","FaFileContract","FaShieldAlt","FaHandshake",
  "FaSearch","FaChartLine","FaUserTie","FaLightbulb","FaBullhorn","FaCheckCircle",
  "FaGavel","FaGlobe","FaLayerGroup","FaCog","FaDatabase",
];
const ACH_ICON_KEYS  = ["FaGraduationCap","FaTrophy","FaMedal","FaAward","FaCertificate","FaStar","FaBookOpen"];
const ACH_BADGE_KEYS = ["FaCrown","FaStar","FaGem","FaBolt","FaRocket","FaMagic","FaFire","FaHeart"];
const COLOR_PRESETS  = ["#C25B2A","#D4AF37","#8B4513","#CD7F32","#6B4226","#5B7E91","#7A8C3E"];

const SEED_SKILLS = [
  { order:1, title:"Contract Drafting, Review & Negotiation", iconKey:"FaFileContract", color:"#C25B2A", statsCount:"800+", statsLabel:"Contracts Reviewed", achievements:["Reviewed 800+ contracts including MSAs, NDAs, SOWs, and commercial agreements","Reduced contract turnaround time by 30% through efficient negotiation","Drove consistency in redlining practices across global teams"], level:98 },
  { order:2, title:"Contract Lifecycle & Workflow Optimisation", iconKey:"FaChartLine", color:"#D4AF37", statsCount:"500+", statsLabel:"Contracts Managed", achievements:["Managed end-to-end lifecycle of 500+ contracts","Leveraged OneTrust, Evisort, DocuSign, and SharePoint platforms","Achieved 40% reduction in approval delays"], level:95 },
  { order:3, title:"Data Privacy & Compliance", iconKey:"FaShieldAlt", color:"#8B4513", statsCount:"100+", statsLabel:"GDPR Reviews", achievements:["Delivered GDPR compliance services for Swiss multinational","Interpreted 200+ regulatory publications from FCA, PRA, ESMA","Facilitated negotiations enabling 25% faster closure"], level:94 },
  { order:4, title:"Stakeholder & Cross-Functional Collaboration", iconKey:"FaHandshake", color:"#C25B2A", statsCount:"Global", statsLabel:"Stakeholder Base", achievements:["Collaborated across Legal, Procurement, Sales, HR, Finance teams","Supported vendor negotiations and supplier due diligence","Facilitated coordination for smooth contractual implementation"], level:96 },
  { order:5, title:"Legal Research & Strategic Guidance", iconKey:"FaSearch", color:"#D4AF37", statsCount:"3+", statsLabel:"Sales Pitch Decks", achievements:["Conducted comprehensive legal research across multiple domains","Collaborated on sales pitch decks for client engagement","Analysed market trends to identify business opportunities"], level:93 },
  { order:6, title:"Process Optimisation & Business Savings", iconKey:"FaLightbulb", color:"#8B4513", statsCount:"$750K+", statsLabel:"Business Savings", achievements:["Realized over $750K in savings through pricing analysis","Developed playbooks with fallback provisions","Designed SOPs, workflows, and checklists for team performance"], level:92 },
  { order:7, title:"Training & Knowledge Sharing", iconKey:"FaUserTie", color:"#C25B2A", statsCount:"25+", statsLabel:"Professionals Trained", achievements:["Mentored 10+ legal analysts, reducing onboarding by 50%","Delivered training on GDPR and contract workflows","Designed knowledge-sharing materials for cross-functional teams"], level:90 },
  { order:8, title:"Brand Visibility & Marketing", iconKey:"FaBullhorn", color:"#D4AF37", statsCount:"40%", statsLabel:"Engagement Boost", achievements:["Boosted social media engagement by 40%","Supported investor outreach initiatives","Contributed to marketing and brand visibility campaigns"], level:88 },
  { order:9, title:"Document Review & Contract Abstraction", iconKey:"FaCheckCircle", color:"#8B4513", statsCount:"98%+", statsLabel:"Accuracy Rate", achievements:["Conducted first-level review for MSAs, SOWs, NDAs, DPAs","Abstracted 100+ metadata points per contract","Delivered 100% on-time in high-volume LPO environments"], level:97 },
];

const SEED_ACHIEVEMENTS = [
  { order:1, title:"LLM with Distinction", subtitle:"Queen Mary University of London", description:"Completed Master of Laws with Distinction, specializing in International Commercial Law with advanced focus on contract automation and legal technology", color:"#C25B2A", year:"2023", iconKey:"FaGraduationCap", badgeKey:"FaCrown" },
  { order:2, title:"Academic Excellence", subtitle:"Ranked 6th out of 5,500+ students", description:"Received Merit Certificate from Pune University, demonstrating exceptional academic performance and dedication to legal studies", color:"#D4AF37", year:"2022", iconKey:"FaTrophy", badgeKey:"FaStar" },
  { order:3, title:"Multiple Merit Scholarships", subtitle:"Recognition for Academic Excellence", description:"Recipient of multiple merit-based scholarships in recognition of outstanding academic achievement and leadership potential", color:"#CD7F32", year:"2021", iconKey:"FaMedal", badgeKey:"FaGem" },
  { order:4, title:"Student Leadership", subtitle:"President, Balaji Law College", description:"Demonstrated strong leadership by overseeing student initiatives, organizing institutional legal fests, and actively advocating for student welfare", color:"#C25B2A", year:"2020", iconKey:"FaAward", badgeKey:"FaBolt" },
  { order:5, title:"Legal Tech Recognition", subtitle:"Contract Automation Excellence", description:"Earned high academic recognition in Legal Tech for innovative work on contract automation, exploring how technology transforms the contracting process", color:"#D4AF37", year:"2023", iconKey:"FaCertificate", badgeKey:"FaRocket" },
  { order:6, title:"Course Representative", subtitle:"Queen Mary University of London", description:"Represented academic interests of the LLM cohort, liaising with faculty to enhance educational experience and student engagement", color:"#8B4513", year:"2023", iconKey:"FaStar", badgeKey:"FaMagic" },
];

const DEFAULT_SKILL = { order:1, title:"", iconKey:"FaFileContract", color:"#C25B2A", statsCount:"", statsLabel:"", achievements:[""], level:90 };
const DEFAULT_ACH   = { order:1, title:"", subtitle:"", description:"", color:"#C25B2A", year:String(new Date().getFullYear()), iconKey:"FaTrophy", badgeKey:"FaStar" };
const DEFAULT_CONFIG = {
  heroTitle:"Skills & Achievements",
  heroSubtitle:"A comprehensive showcase of legal expertise, professional accomplishments, and proven track record in delivering exceptional results",
  heroBadgeText:"Professional Excellence",
  heroBadgeIcon:"FaGem",
  heroStats:[{ value:"800+", label:"Contracts" },{ value:"$750K+", label:"Savings" },{ value:"98%+", label:"Accuracy" }],
  sectionCompetenciesTitle:"Core Competencies",
  sectionAchievementsTitle:"Academic Excellence",
  closingTitle:"Commitment to Excellence",
  closingText:"These skills and achievements represent years of dedication, continuous learning, and unwavering commitment to delivering exceptional legal services.",
  commandCockpitLabel:"Capability cockpit",
  proofLabel:"Evidence log",
  accentColor1:"#C25B2A",
  accentColor2:"#D4AF37",
};

/* ── COLOUR ROW ─────────────────────────────────────────────────── */
function ColorRow({ value, onChange }) {
  return (
    <div className="adm-color-row">
      {COLOR_PRESETS.map(c => (
        <button key={c} type="button"
          className={`adm-color-swatch ${value === c ? "active" : ""}`}
          style={{ background: c }} onClick={() => onChange(c)} />
      ))}
      <input type="color" value={value || "#C25B2A"} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

/* ── SKILL FORM ─────────────────────────────────────────────────── */
function SkillForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setAch = (i, v) => { const a = [...f.achievements]; a[i] = v; set("achievements", a); };
  const addAch = () => set("achievements", [...f.achievements, ""]);
  const removeAch = i => set("achievements", f.achievements.filter((_, j) => j !== i));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">{f.id ? "Edit Skill" : "Add Skill"}</h3>
      <div className="adm-form-grid">
        <label><span>Order</span>
          <input type="number" value={f.order} onChange={e => set("order", e.target.value)} min={1} />
        </label>
        <label><span>Title *</span>
          <input type="text" value={f.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Contract Drafting" />
        </label>
        <label><span>Stat Count</span>
          <input type="text" value={f.statsCount} onChange={e => set("statsCount", e.target.value)} placeholder="800+" />
        </label>
        <label><span>Stat Label</span>
          <input type="text" value={f.statsLabel} onChange={e => set("statsLabel", e.target.value)} placeholder="Contracts Reviewed" />
        </label>
        <label><span>Icon key</span>
          <select value={f.iconKey} onChange={e => set("iconKey", e.target.value)}>
            {SKILL_ICON_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label><span>Level (0–100)</span>
          <input type="number" value={f.level} onChange={e => set("level", e.target.value)} min={0} max={100} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Accent Color</span>
          <ColorRow value={f.color} onChange={v => set("color", v)} />
        </label>
      </div>

      <div className="adm-achievements-editor">
        <span className="adm-field-label">Achievement Bullets</span>
        {f.achievements.map((a, i) => (
          <div key={i} className="adm-ach-row">
            <input type="text" value={a} onChange={e => setAch(i, e.target.value)} placeholder={`Point ${i + 1}`} />
            <button type="button" className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => removeAch(i)}>✕</button>
          </div>
        ))}
        <button type="button" className="adm-btn adm-btn-sm" onClick={addAch}>+ Add Point</button>
      </div>

      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving || !f.title.trim()}>
          {saving ? "Saving…" : f.id ? "Update Skill" : "Add Skill"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── ACHIEVEMENT FORM ───────────────────────────────────────────── */
function AchForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">{f.id ? "Edit Achievement" : "Add Achievement"}</h3>
      <div className="adm-form-grid">
        <label><span>Order</span>
          <input type="number" value={f.order} onChange={e => set("order", e.target.value)} min={1} />
        </label>
        <label><span>Title *</span>
          <input type="text" value={f.title} onChange={e => set("title", e.target.value)} />
        </label>
        <label><span>Subtitle</span>
          <input type="text" value={f.subtitle} onChange={e => set("subtitle", e.target.value)} />
        </label>
        <label><span>Year</span>
          <input type="text" value={f.year} onChange={e => set("year", e.target.value)} />
        </label>
        <label><span>Main Icon</span>
          <select value={f.iconKey} onChange={e => set("iconKey", e.target.value)}>
            {ACH_ICON_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label><span>Badge Icon</span>
          <select value={f.badgeKey} onChange={e => set("badgeKey", e.target.value)}>
            {ACH_BADGE_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Color</span>
          <ColorRow value={f.color} onChange={v => set("color", v)} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Description</span>
          <textarea value={f.description} onChange={e => set("description", e.target.value)} rows={3} />
        </label>
      </div>
      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving || !f.title.trim()}>
          {saving ? "Saving…" : f.id ? "Update" : "Add"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── PAGE CONFIG FORM ───────────────────────────────────────────── */
function ConfigForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setStat = (i, k, v) => {
    const arr = [...(f.heroStats || [])];
    arr[i] = { ...arr[i], [k]: v };
    set("heroStats", arr);
  };
  const addStat    = () => set("heroStats", [...(f.heroStats || []), { value:"", label:"" }]);
  const removeStat = i  => set("heroStats", (f.heroStats || []).filter((_, j) => j !== i));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">Page Design Config</h3>

      <h4 className="adm-form-subheading">Hero</h4>
      <div className="adm-form-grid">
        <label><span>Hero Title</span>
          <input type="text" value={f.heroTitle || ""} onChange={e => set("heroTitle", e.target.value)} />
        </label>
        <label><span>Badge Text</span>
          <input type="text" value={f.heroBadgeText || ""} onChange={e => set("heroBadgeText", e.target.value)} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Hero Subtitle</span>
          <textarea value={f.heroSubtitle || ""} onChange={e => set("heroSubtitle", e.target.value)} rows={2} />
        </label>
        <label><span>Accent Color 1</span>
          <ColorRow value={f.accentColor1} onChange={v => set("accentColor1", v)} />
        </label>
        <label><span>Accent Color 2</span>
          <ColorRow value={f.accentColor2} onChange={v => set("accentColor2", v)} />
        </label>
      </div>

      <h4 className="adm-form-subheading">Hero Stats</h4>
      {(f.heroStats || []).map((stat, i) => (
        <div key={i} className="adm-stat-row">
          <input type="text" value={stat.value} onChange={e => setStat(i,"value",e.target.value)} placeholder="800+" />
          <input type="text" value={stat.label} onChange={e => setStat(i,"label",e.target.value)} placeholder="Contracts" />
          <button type="button" className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => removeStat(i)}>✕</button>
        </div>
      ))}
      <button type="button" className="adm-btn adm-btn-sm" onClick={addStat}>+ Add Stat</button>

      <h4 className="adm-form-subheading">Section Labels</h4>
      <div className="adm-form-grid">
        <label><span>Competencies Title</span>
          <input type="text" value={f.sectionCompetenciesTitle || ""} onChange={e => set("sectionCompetenciesTitle", e.target.value)} />
        </label>
        <label><span>Achievements Title</span>
          <input type="text" value={f.sectionAchievementsTitle || ""} onChange={e => set("sectionAchievementsTitle", e.target.value)} />
        </label>
        <label><span>Cockpit Label</span>
          <input type="text" value={f.commandCockpitLabel || ""} onChange={e => set("commandCockpitLabel", e.target.value)} />
        </label>
        <label><span>Proof Label</span>
          <input type="text" value={f.proofLabel || ""} onChange={e => set("proofLabel", e.target.value)} />
        </label>
        <label><span>Closing Title</span>
          <input type="text" value={f.closingTitle || ""} onChange={e => set("closingTitle", e.target.value)} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Closing Text</span>
          <textarea value={f.closingText || ""} onChange={e => set("closingText", e.target.value)} rows={3} />
        </label>
      </div>

      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving}>
          {saving ? "Saving…" : "Save Config"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── MAIN PANEL ─────────────────────────────────────────────────── */
export default function SkillsPanel() {
  const [skills,       setSkills]       = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [config,       setConfig]       = useState(null);
  const [tab,          setTab]          = useState("skills");
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [seeding,      setSeeding]      = useState(false);
  const [skillForm,    setSkillForm]    = useState(null);
  const [achForm,      setAchForm]      = useState(null);
  const [configForm,   setConfigForm]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sSnap, aSnap, cSnap] = await Promise.all([
        getDocs(query(collection(db, "skills_data"),       orderBy("order","asc"))),
        getDocs(query(collection(db, "achievements_data"), orderBy("order","asc"))),
        getDocs(collection(db, "page_config")),
      ]);
      setSkills(sSnap.docs.map(d => ({ id:d.id, ...d.data() })));
      setAchievements(aSnap.docs.map(d => ({ id:d.id, ...d.data() })));
      const cd = cSnap.docs.find(d => d.id === "skills");
      setConfig(cd ? { id:"skills", ...cd.data() } : { id:"skills", ...DEFAULT_CONFIG });
    } catch(e) { console.error(e); }
    finally    { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Skills CRUD ── */
  const saveSkill = async (form) => {
    setSaving(true);
    try {
      const { id, ...data } = form;
      const clean = { ...data, achievements:(data.achievements||[]).filter(a=>a.trim()), level:Number(data.level), order:Number(data.order) };
      if (id) await setDoc(doc(db,"skills_data",id), clean);
      else    await addDoc(collection(db,"skills_data"), clean);
      setSkillForm(null); await load();
    } finally { setSaving(false); }
  };
  const deleteSkill = async (id) => {
    if (!window.confirm("Delete this skill?")) return;
    await deleteDoc(doc(db,"skills_data",id)); await load();
  };

  /* ── Achievements CRUD ── */
  const saveAch = async (form) => {
    setSaving(true);
    try {
      const { id, ...data } = form;
      const clean = { ...data, order:Number(data.order) };
      if (id) await setDoc(doc(db,"achievements_data",id), clean);
      else    await addDoc(collection(db,"achievements_data"), clean);
      setAchForm(null); await load();
    } finally { setSaving(false); }
  };
  const deleteAch = async (id) => {
    if (!window.confirm("Delete this achievement?")) return;
    await deleteDoc(doc(db,"achievements_data",id)); await load();
  };

  /* ── Config ── */
  const saveConfig = async (form) => {
    setSaving(true);
    try {
      const { id, ...data } = form;
      await setDoc(doc(db,"page_config","skills"), data);
      setConfigForm(null); await load();
    } finally { setSaving(false); }
  };

  /* ── Seed ── */
  const seedAll = async () => {
    if (!window.confirm("Seed all default skills + achievements to Firestore? Existing documents will NOT be overwritten.")) return;
    setSeeding(true);
    try {
      await Promise.all([
        ...SEED_SKILLS.map(s => addDoc(collection(db,"skills_data"), s)),
        ...SEED_ACHIEVEMENTS.map(a => addDoc(collection(db,"achievements_data"), a)),
        setDoc(doc(db,"page_config","skills"), DEFAULT_CONFIG),
      ]);
      await load();
    } catch(e) { alert("Seed error: " + e.message); }
    finally    { setSeeding(false); }
  };

  if (loading && !skills.length) return <div className="adm-panel-loading">Loading Skills data…</div>;

  return (
    <section className="adm-panel">
      <div className="adm-panel-head">
        <div>
          <h2>Skills &amp; Achievements</h2>
          <p>Manage skills, academic milestones, and page design</p>
        </div>
        <div className="adm-panel-head-right">
          {skills.length === 0 && (
            <button className="adm-btn adm-btn-seed" onClick={seedAll} disabled={seeding}>
              {seeding ? "Seeding…" : "Seed Default Data"}
            </button>
          )}
        </div>
      </div>

      <div className="adm-tab-row">
        {[["skills","Skills"],["achievements","Achievements"],["config","Page Design"]].map(([v,l]) => (
          <button key={v} className={`adm-tab-btn ${tab===v?"active":""}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {/* ── SKILLS ── */}
      {tab === "skills" && (
        <div className="adm-tab-content">
          <div className="adm-panel-toolbar">
            <button className="adm-btn adm-btn-primary"
              onClick={() => setSkillForm({ ...DEFAULT_SKILL, order:skills.length+1 })}>
              + Add Skill
            </button>
            <span className="adm-count-badge">{skills.length} skills</span>
          </div>
          {skillForm && <SkillForm form={skillForm} setForm={setSkillForm} onSave={saveSkill} onCancel={() => setSkillForm(null)} saving={saving} />}
          <div className="adm-list">
            {skills.length === 0 && <p className="adm-empty">No skills yet — click "Seed Default Data" above or add manually.</p>}
            {skills.map(s => (
              <div key={s.id} className="adm-list-row" style={{ "--row-color": s.color }}>
                <div className="adm-list-row-left">
                  <div className="adm-list-order">{s.order}</div>
                  <div>
                    <div className="adm-list-title">{s.title}</div>
                    <div className="adm-list-meta">{s.statsCount} {s.statsLabel} · Level {s.level}%</div>
                  </div>
                </div>
                <div className="adm-list-actions">
                  <button className="adm-btn adm-btn-sm" onClick={() => setSkillForm(s)}>Edit</button>
                  <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteSkill(s.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ACHIEVEMENTS ── */}
      {tab === "achievements" && (
        <div className="adm-tab-content">
          <div className="adm-panel-toolbar">
            <button className="adm-btn adm-btn-primary"
              onClick={() => setAchForm({ ...DEFAULT_ACH, order:achievements.length+1 })}>
              + Add Achievement
            </button>
            <span className="adm-count-badge">{achievements.length} achievements</span>
          </div>
          {achForm && <AchForm form={achForm} setForm={setAchForm} onSave={saveAch} onCancel={() => setAchForm(null)} saving={saving} />}
          <div className="adm-list">
            {achievements.length === 0 && <p className="adm-empty">No achievements yet.</p>}
            {achievements.map(a => (
              <div key={a.id} className="adm-list-row" style={{ "--row-color": a.color }}>
                <div className="adm-list-row-left">
                  <div className="adm-list-order">{a.order}</div>
                  <div>
                    <div className="adm-list-title">{a.title}</div>
                    <div className="adm-list-meta">{a.subtitle} · {a.year}</div>
                  </div>
                </div>
                <div className="adm-list-actions">
                  <button className="adm-btn adm-btn-sm" onClick={() => setAchForm(a)}>Edit</button>
                  <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteAch(a.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PAGE DESIGN ── */}
      {tab === "config" && (
        <div className="adm-tab-content">
          {configForm
            ? <ConfigForm form={configForm} setForm={setConfigForm} onSave={saveConfig} onCancel={() => setConfigForm(null)} saving={saving} />
            : (
              <div className="adm-config-preview">
                <div className="adm-config-row"><strong>Hero Title</strong><span>{config?.heroTitle}</span></div>
                <div className="adm-config-row"><strong>Badge Text</strong><span>{config?.heroBadgeText}</span></div>
                <div className="adm-config-row">
                  <strong>Accents</strong>
                  <span>
                    <span className="adm-color-dot" style={{ background:config?.accentColor1 }} />{config?.accentColor1}
                    &nbsp;&amp;&nbsp;
                    <span className="adm-color-dot" style={{ background:config?.accentColor2 }} />{config?.accentColor2}
                  </span>
                </div>
                <div className="adm-config-row"><strong>Competencies Heading</strong><span>{config?.sectionCompetenciesTitle}</span></div>
                <div className="adm-config-row"><strong>Achievements Heading</strong><span>{config?.sectionAchievementsTitle}</span></div>
                <div className="adm-config-row"><strong>Closing Title</strong><span>{config?.closingTitle}</span></div>
                <button className="adm-btn adm-btn-primary" onClick={() => setConfigForm({ ...DEFAULT_CONFIG, ...config })}>
                  Edit Page Design
                </button>
              </div>
            )
          }
        </div>
      )}
    </section>
  );
}
